import { defaultCoordinates, KeyboardCode, } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { add as getAdjustedCoordinates, getOwnerDocument, getWindow, isKeyboardEvent, subtract as getCoordinatesDelta, } from '@dnd-kit/utilities';
import { EventName } from '../events';
import { Listeners } from '../Listeners';
import { scrollIntoViewIfNeeded } from '../scrollUtil';
import { defaultActivators } from './defaults';
import { scrollToNewCoordinates } from './helpers';
/**
 * Custom Keyboard Sensor where a holding certain keys (Alt + Shift by default)
 * will start dragging an item. Uplifting the keys will drop the item
 * and pressing Escape will cancel the dragging.
 *
 * This is a different scenario from the default KeyboardSensor which needs
 * separate keydown actions for lifting and dropping items.
 *
 * You can override this setting by passing optional parameter keyboardCodes
 * with activate and cancel properties.
 */
var CustomKeyboardSensor = /** @class */ (function () {
    function CustomKeyboardSensor(props) {
        this.props = props;
        this.autoScrollEnabled = false;
        var target = props.event.target;
        this.props = props;
        this.listeners = new Listeners(getOwnerDocument(target));
        this.windowListeners = new Listeners(getWindow(target));
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.attach();
    }
    CustomKeyboardSensor.prototype.attach = function () {
        var _this = this;
        var _a;
        this.handleStart();
        this.windowListeners.add(EventName.Resize, this.handleCancel);
        this.windowListeners.add(EventName.VisibilityChange, this.handleCancel);
        (_a = this.props.event.target) === null || _a === void 0 ? void 0 : _a.addEventListener(EventName.Blur, this.handleCancel);
        setTimeout(function () {
            _this.listeners.add(EventName.Keydown, _this.handleKeyDown);
            _this.listeners.add(EventName.Keyup, _this.handleKeyUp);
        });
    };
    CustomKeyboardSensor.prototype.handleStart = function () {
        var _a = this.props, activeNode = _a.activeNode, onStart = _a.onStart;
        var node = activeNode.node.current;
        if (node) {
            scrollIntoViewIfNeeded(node);
        }
        onStart(defaultCoordinates);
    };
    CustomKeyboardSensor.prototype.handleKeyDown = function (event) {
        if (isKeyboardEvent(event)) {
            var _a = this.props, active = _a.active, context = _a.context, options = _a.options;
            var keyboardCodes = options.keyboardCodes, _b = options.coordinateGetter, coordinateGetter = _b === void 0 ? sortableKeyboardCoordinates : _b, _c = options.scrollBehavior, scrollBehavior = _c === void 0 ? 'smooth' : _c;
            var code = event.code;
            if ((keyboardCodes && keyboardCodes.cancel.includes(code)) ||
                (!keyboardCodes && code === KeyboardCode.Esc)) {
                this.handleCancel(event);
                return;
            }
            var collisionRect = context.current.collisionRect;
            var currentCoordinates = collisionRect
                ? { x: collisionRect.left, y: collisionRect.top }
                : defaultCoordinates;
            if (!this.referenceCoordinates) {
                this.referenceCoordinates = currentCoordinates;
            }
            var newCoordinates = coordinateGetter(event, {
                active: active,
                context: context.current,
                currentCoordinates: currentCoordinates,
            });
            if (newCoordinates) {
                var scrollableAncestors = context.current.scrollableAncestors;
                var scrollDelta = scrollToNewCoordinates(scrollableAncestors, newCoordinates, currentCoordinates, event.code, scrollBehavior);
                this.handleMove(event, getAdjustedCoordinates(getCoordinatesDelta(newCoordinates, this.referenceCoordinates), scrollDelta));
            }
        }
    };
    CustomKeyboardSensor.prototype.handleKeyUp = function (event) {
        if (isKeyboardEvent(event)) {
            var keyboardCodes = this.props.options.keyboardCodes;
            var code = event.code, altKey = event.altKey, shiftKey = event.shiftKey;
            // stop dragging if keyboard code exists and includes key up or default lifting of either Alt or Shift key
            if ((keyboardCodes && !keyboardCodes.activate.includes(code)) ||
                (!keyboardCodes && (!altKey || !shiftKey))) {
                event.preventDefault();
                this.handleEnd(event);
            }
        }
    };
    CustomKeyboardSensor.prototype.handleMove = function (event, coordinates) {
        var onMove = this.props.onMove;
        event.preventDefault();
        onMove(coordinates);
    };
    CustomKeyboardSensor.prototype.handleEnd = function (event) {
        var onEnd = this.props.onEnd;
        event.preventDefault();
        this.detach();
        onEnd();
    };
    CustomKeyboardSensor.prototype.handleCancel = function (event) {
        var onCancel = this.props.onCancel;
        event.preventDefault();
        this.detach();
        onCancel();
    };
    CustomKeyboardSensor.prototype.detach = function () {
        var _a;
        this.listeners.removeAll();
        this.windowListeners.removeAll();
        (_a = this.props.event.target) === null || _a === void 0 ? void 0 : _a.removeEventListener(EventName.Blur, this.handleCancel);
    };
    CustomKeyboardSensor.activators = defaultActivators;
    return CustomKeyboardSensor;
}());
export { CustomKeyboardSensor };
//# sourceMappingURL=customKeyboardSensor.js.map